import { hasRemovedMarkingClass, MARKING_EXCLUDED_ATTRIBUTE, MARKING_INCLUDED_ATTRIBUTE } from '#shared/utils/markings/rules'

/**
 * Ibexa content uses `.reference` for two different things: the footnote
 * marker, a span wrapping hidden note text, and the law reference button,
 * whose label — «krrl. § 17» — is part of the sentence around it. Only the
 * first is chrome; dropping the second leaves a hole in the excerpt.
 *
 * Quotes are stripped from the class before matching: academic article
 * content reaches the DOM as `class="\"reference\""`, because the source
 * escapes its attributes and this path never runs `clean-corrupted-attributes`.
 * `data-id`, which would identify the button directly, is dropped by the
 * richText sanitizer (ALLOW_DATA_ATTR: false).
 */
export function isInlineLawReference(el: Element): boolean {
  if (el.tagName !== 'BUTTON') {
    return false
  }

  const classes = (el.getAttribute('class') ?? '').replace(/[\\"']/g, ' ').split(/\s+/).filter(Boolean)

  // `reference-card-*` is the injected card chrome, not content.
  return classes.includes('reference') && !classes.some(name => name.startsWith('reference-card'))
}

/**
 * Shared noise predicate for capture, wrap and locate. Skips chrome marked
 * with `data-marking-exclude`, the classes the shared rules drop (footnote
 * toggles and bodies, `.reference` markers, the `.page-number` column), and
 * text outside `.body-text` within a citation parent. Falls back to "include"
 * when the parent has no `.body-text` — non-destructive on unclassified
 * content surfaces, which is why law chrome marks itself excluded instead.
 * `data-marking-content` opts a subtree back in: section titles render beside
 * the wrapper rather than inside it, and are content rather than chrome.
 */
export function isNoiseTextNode(node: Node, citationParent: Element | null): boolean {
  if (node.nodeType !== Node.TEXT_NODE) {
    return false
  }

  let el: Element | null = node.parentElement
  let insideBodyText = false
  let insideMarkableContent = false

  while (el && el !== document.body) {
    if (el.hasAttribute(MARKING_EXCLUDED_ATTRIBUTE)) {
      return true
    }

    if (!isInlineLawReference(el) && hasRemovedMarkingClass(el.getAttribute('class'))) {
      return true
    }

    if (el.classList.contains('body-text')) {
      insideBodyText = true
    }

    if (el.hasAttribute(MARKING_INCLUDED_ATTRIBUTE)) {
      insideMarkableContent = true
    }

    if (citationParent && el === citationParent) {
      break
    }

    el = el.parentElement
  }

  if (citationParent && !insideMarkableContent && hasBodyTextDescendant(citationParent) && !insideBodyText) {
    return true
  }

  return false
}

const bodyTextLookup = new WeakMap<Element, boolean>()

function hasBodyTextDescendant(parent: Element): boolean {
  const cached = bodyTextLookup.get(parent)

  if (cached !== undefined) {
    return cached
  }

  const has = !!parent.querySelector('.body-text')
  bodyTextLookup.set(parent, has)
  return has
}

export function* iterateMarkableTextNodes(
  root: Node,
  citationParent: Element | null,
): Generator<Text> {
  if (root instanceof Text) {
    if (!isNoiseTextNode(root, citationParent)) {
      yield root
    }

    return
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      return isNoiseTextNode(node, citationParent)
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT
    },
  })
  let node: Node | null = walker.nextNode()

  while (node) {
    if (node instanceof Text) {
      yield node
    }

    node = walker.nextNode()
  }
}

/**
 * The markable text a root owns itself, stopping at nested citation roots.
 * Law nests them: a § root holds the heading, each ledd is a root inside it.
 * Walking the whole subtree would store the ledd text twice, so a root
 * contributes only what lies between its own boundaries.
 */
export function* iterateOwnMarkableTextNodes(root: Element): Generator<Text> {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (node instanceof Element) {
        return node !== root && node.hasAttribute('data-citation-source')
          ? NodeFilter.FILTER_REJECT
          : NodeFilter.FILTER_SKIP
      }

      return isNoiseTextNode(node, root) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT
    },
  })
  let node: Node | null = walker.nextNode()

  while (node) {
    if (node instanceof Text) {
      yield node
    }

    node = walker.nextNode()
  }
}

export function findCitationParent(node: Node): Element | null {
  let el: Element | null = node instanceof Element ? node : node.parentElement

  while (el && el !== document.body) {
    if (el.getAttribute('data-citation-source')) {
      return el
    }

    el = el.parentElement
  }

  return null
}

/**
 * The marking-visible text of a root, as one string. Capture and rehydration
 * must derive offsets from the SAME traversal — a second implementation that
 * drifts would silently anchor markings to the wrong text.
 */
export function collectMarkableText(root: Element): string {
  const citationParent = findCitationParent(root) ?? root
  let text = ''

  for (const node of iterateMarkableTextNodes(root, citationParent)) {
    text += node.data
  }

  return text
}
